import { useState } from 'react';
import { Sparkles, AlertTriangle, CheckCircle2, XCircle, Loader2, FileSearch, ListChecks, BookOpen } from 'lucide-react';
import { aiApi, type GrantPrecheckResult } from '../../services/api';

const SEVERITY_STYLE: Record<string, { bg: string; text: string; label: string }> = {
  critical: { bg: 'bg-red-50 border-red-200', text: 'text-red-700', label: 'Krytyczne' },
  high: { bg: 'bg-orange-50 border-orange-200', text: 'text-orange-700', label: 'Wysokie' },
  medium: { bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700', label: 'Średnie' },
  low: { bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', label: 'Niskie' },
};

function scoreColor(score: number): string {
  if (score >= 80) return 'text-green-600';
  if (score >= 60) return 'text-amber-600';
  return 'text-red-600';
}

interface Props {
  applicationId: number;
  productCode?: string;
}

/**
 * AI compliance precheck panel for grant / dev-grant applications.
 * Calls POST /api/ai-modules/grant-writer/precheck-application (RAG over kb_chunks + Azure OpenAI).
 * Human-in-the-loop: read-only suggestion, never mutates the application.
 */
export function GrantPrecheckPanel({ applicationId, productCode }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GrantPrecheckResult | null>(null);
  const [sources, setSources] = useState<string[]>([]);

  const runPrecheck = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiApi.precheckApplication(applicationId, productCode);
      // Backend may return a parsed object or, on parse failure, a string — guard both
      const pc: any = data.precheck;
      if (!pc || typeof pc !== 'object' || typeof pc.score_0_100 !== 'number') {
        setError('AI zwróciło odpowiedź w nieoczekiwanym formacie. Spróbuj ponownie.');
        setResult(null);
      } else {
        setResult(pc as GrantPrecheckResult);
        setSources(Array.isArray(data.kb_sources) ? data.kb_sources : []);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Błąd walidacji wniosku przez AI. Spróbuj ponownie.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="text-lg font-bold flex items-center gap-2">
          <Sparkles size={22} className="text-violet-600" />
          Sprawdzenie AI — zgodność dotacji
        </h3>
        <button
          onClick={runPrecheck}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <FileSearch size={16} />}
          {loading ? 'Analizuję wniosek…' : result ? 'Sprawdź ponownie' : 'Sprawdź AI'}
        </button>
      </div>

      <p className="text-xs text-slate-500 mt-1">
        Weryfikacja kompletności i zgodności z regulaminami naboru (RAG z bazy wiedzy + Azure OpenAI). Sugestia — nie zmienia wniosku.
      </p>

      {error && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-5 space-y-5">
          {/* Score + compliance */}
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex items-baseline gap-1">
              <span className={`text-4xl font-extrabold ${scoreColor(result.score_0_100)}`}>{result.score_0_100}</span>
              <span className="text-slate-400 text-lg font-semibold">/100</span>
            </div>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${
                result.compliant ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
              }`}
            >
              {result.compliant ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
              {result.compliant ? 'Zgodny z wymogami' : 'Wymaga poprawek'}
            </span>
          </div>

          {result.summary && <p className="text-sm text-slate-700 leading-relaxed">{result.summary}</p>}

          {/* Issues */}
          {Array.isArray(result.issues) && result.issues.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                <AlertTriangle size={15} className="text-amber-500" /> Problemy do poprawy ({result.issues.length})
              </h4>
              <div className="space-y-2">
                {result.issues.map((iss, i) => {
                  const s = SEVERITY_STYLE[iss.severity] || SEVERITY_STYLE.low;
                  return (
                    <div key={i} className={`p-3 rounded-lg border ${s.bg}`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-xs font-bold uppercase ${s.text}`}>{s.label}</span>
                        {iss.field && <span className="text-xs text-slate-500 font-mono">{iss.field}</span>}
                      </div>
                      <p className="text-sm text-slate-700">{iss.problem}</p>
                      {iss.recommendation && (
                        <p className="text-sm text-slate-600 mt-1">
                          <span className="font-medium">Rekomendacja:</span> {iss.recommendation}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Missing data */}
          {Array.isArray(result.missing_data) && result.missing_data.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                <ListChecks size={15} className="text-red-500" /> Brakujące dane
              </h4>
              <ul className="list-disc list-inside text-sm text-slate-700 space-y-0.5">
                {result.missing_data.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          )}

          {/* Strengths */}
          {Array.isArray(result.strengths) && result.strengths.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5">
                <CheckCircle2 size={15} className="text-green-500" /> Mocne strony
              </h4>
              <ul className="list-disc list-inside text-sm text-slate-700 space-y-0.5">
                {result.strengths.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}

          {/* RAG sources */}
          {sources.length > 0 && (
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs text-slate-500 flex items-center gap-1.5 flex-wrap">
                <BookOpen size={13} /> Źródła z bazy wiedzy:
                {sources.map((src, i) => (
                  <span key={i} className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">{src}</span>
                ))}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
